/**
 * OsintDashboard — Vista Analista / Producto de Inteligencia
 *
 * Tres paneles integrados:
 *   1. Grafo de relaciones (Cytoscape.js + layout dagre)
 *   2. Timeline cronológico de eventos (artículos + alertas)
 *   3. Panel de evidencias ancladas (pin · visualizar · exportar)
 *
 * Se monta sobre los datos ya cargados del IntelProfilePanel (sin nueva
 * llamada API). Recibe el objeto `perfil` como prop.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";

cytoscape.use(dagre);

// ── Paleta ───────────────────────────────────────────────────────────────────
const NIVEL_COLOR = {
  CRITICO: "#dc2626",
  ALTO:    "#ea580c",
  MEDIO:   "#ca8a04",
  BAJO:    "#16a34a",
};
const NIVEL_BG = {
  CRITICO: "#fef2f2",
  ALTO:    "#fff7ed",
  MEDIO:   "#fefce8",
  BAJO:    "#f0fdf4",
};

// ── Construcción del grafo desde el perfil ───────────────────────────────────

function construirGrafo(perfil) {
  const nodes = [];
  const edges = [];
  const ids   = new Set();

  function addNode(id, label, tipo, nivel = "BAJO", extra = {}) {
    if (ids.has(id)) return;
    ids.add(id);
    nodes.push({ data: { id, label, tipo, nivel, ...extra } });
  }
  function addEdge(source, target, label, peso = 1) {
    const eid = `${source}__${target}__${label}`;
    if (ids.has(eid)) return;
    ids.add(eid);
    edges.push({ data: { id: eid, source, target, label, peso } });
  }

  const sujeto_id  = "sujeto";
  const nivel_sujeto = perfil.nivel_riesgo || "BAJO";
  addNode(sujeto_id, perfil.termino + (perfil.alias ? `\n"${perfil.alias}"` : ""), "sujeto", nivel_sujeto);

  // OFAC
  const ofac = perfil.fuentes?.ofac;
  if (ofac?.en_lista_ofac) {
    const nid = "ofac_sdn";
    addNode(nid, "OFAC SDN", "sancion", "CRITICO", { detalle: `${ofac.total_coincidencias} coincidencias` });
    addEdge(sujeto_id, nid, "SANCIONADO_EN", 3);
    (ofac.coincidencias || []).slice(0, 3).forEach((c, i) => {
      const cid = `ofac_hit_${i}`;
      addNode(cid, c.nombre || "Registro OFAC", "entidad", "CRITICO", { programa: c.programa, tipo: c.tipo, fuente: "OFAC", url_detalle: null });
      addEdge(nid, cid, "REGISTRO", 2);
    });
  }

  // OpenSanctions
  const osanc = perfil.fuentes?.opensanctions;
  if (osanc?.resultados?.length) {
    const nid = "opensanc";
    addNode(nid, "OpenSanctions", "sancion", "ALTO", { detalle: `${osanc.total} registros` });
    addEdge(sujeto_id, nid, "SANCIONADO_EN", 2);
    (osanc.resultados || []).slice(0, 3).forEach((r, i) => {
      const cid = `osanc_${i}`;
      addNode(cid, r.nombre || "Entidad", "entidad", "ALTO", {
        tipo: r.schema, fuente: "OpenSanctions",
        datasets: (r.datasets || []).join(", "),
        url_detalle: r.url || null,
      });
      addEdge(nid, cid, "REGISTRO", 1);
    });
  }

  // Wikipedia
  const wiki = perfil.fuentes?.wikipedia;
  if (wiki?.encontrado) {
    const nid = "wikipedia";
    const nivel_wiki = wiki.es_criminal ? "ALTO" : "BAJO";
    addNode(nid, "Wikipedia", "fuente", nivel_wiki, {
      titulo: wiki.titulo,
      extracto: (wiki.extracto || "").substring(0, 200),
      url_detalle: wiki.url,
    });
    addEdge(sujeto_id, nid, "DOCUMENTADO_EN", 1);
  }

  // Medios — fuente consolidada
  const arts = perfil.fuentes?.articulos || [];
  if (arts.length > 0) {
    const nid = "medios";
    const es_criminal = perfil.fuentes?.ddg?.es_criminal;
    addNode(nid, `Medios (${arts.length})`, "fuente", es_criminal ? "ALTO" : "MEDIO", {
      detalle: `${arts.length} artículos encontrados`,
    });
    addEdge(sujeto_id, nid, "MENCIONADO_EN", 1);

    // Fuentes individuales únicas (max 8)
    const fuentes_unicas = [...new Set(arts.slice(0, 30).map(a => a.fuente).filter(Boolean))].slice(0, 8);
    fuentes_unicas.forEach((f, i) => {
      const fid = `fuente_${i}`;
      const arts_fuente = arts.filter(a => a.fuente === f);
      addNode(fid, f, "medio", "BAJO", { articulos: arts_fuente.length });
      addEdge(nid, fid, "PUBLICADO_EN", 1);
    });
  }

  // Entidades relacionadas
  (perfil.entidades_relacionadas || []).forEach((e, i) => {
    const eid = `entidad_${i}`;
    addNode(eid, e.nombre || "Entidad", "entidad", "MEDIO", {
      tipo: e.tipo, fuente: e.fuente,
      datasets: (e.datasets || []).join(", "),
    });
    addEdge(sujeto_id, eid, "ASOCIADO_A", 1);
  });

  // theHarvester — infraestructura (IPs y hosts)
  const harv = perfil.herramientas_externas?.harvester;
  if (harv?.disponible && harv.total_hallazgos > 0) {
    const hid = "harvester_root";
    addNode(hid, "Infraestructura", "infra", "MEDIO", {
      emails: harv.emails?.length || 0,
      hosts:  harv.hosts?.length  || 0,
      ips:    harv.ips?.length    || 0,
    });
    addEdge(sujeto_id, hid, "OPERA_EN", 2);
    (harv.ips || []).slice(0, 6).forEach((ip, i) => {
      const ipid = `ip_${i}`;
      addNode(ipid, ip, "ip", "BAJO", { tipo: "IP" });
      addEdge(hid, ipid, "ALOJA", 1);
    });
  }

  // Shodan (tipo=ip directo)
  const shodan = perfil.fuentes?.shodan;
  if (shodan && typeof shodan === "object" && !Array.isArray(shodan)) {
    const sid = "shodan_ip";
    addNode(sid, "Shodan", "infra", "MEDIO", { raw: JSON.stringify(shodan).substring(0, 100) });
    addEdge(sujeto_id, sid, "EXPUESTO_EN", 2);
  }

  return { nodes, edges };
}

// ── Construcción del timeline ────────────────────────────────────────────────

function construirTimeline(perfil) {
  const eventos = [];

  // Artículos con fecha
  const arts = [
    ...(perfil.fuentes?.articulos || []),
    ...(perfil.fuentes?.ddg?.articulos_web || []),
    ...(perfil.fuentes?.google?.articulos  || []),
  ];
  arts.forEach((a, i) => {
    if (!a.fecha) return;
    eventos.push({
      id: `art_${i}`,
      fecha: a.fecha.substring(0, 10),
      titulo: a.titulo || "(sin título)",
      fuente: a.fuente || "Web",
      url: a.url || null,
      descripcion: a.descripcion || "",
      nivel: "BAJO",
      tipo: "articulo",
    });
  });

  // Alertas (sin fecha exacta — agrupadas al inicio)
  (perfil.alertas || []).forEach((a, i) => {
    eventos.push({
      id: `alerta_${i}`,
      fecha: null,
      titulo: a.mensaje,
      fuente: a.fuente || "Análisis",
      url: null,
      descripcion: "",
      nivel: a.nivel || "BAJO",
      tipo: "alerta",
    });
  });

  // Alertas CDR
  (perfil.alertas_cdr || []).forEach((a, i) => {
    eventos.push({
      id: `cdr_${i}`,
      fecha: null,
      titulo: a.mensaje,
      fuente: a.fuente || "CDR",
      url: null,
      descripcion: "",
      nivel: a.nivel || "BAJO",
      tipo: "cdr",
    });
  });

  // Ordenar: primero sin fecha (alertas), luego por fecha desc
  eventos.sort((a, b) => {
    if (!a.fecha && !b.fecha) return 0;
    if (!a.fecha) return -1;
    if (!b.fecha) return 1;
    return b.fecha.localeCompare(a.fecha);
  });

  return eventos;
}

// ── Componente Grafo ─────────────────────────────────────────────────────────

const TIPO_STYLE = {
  sujeto:  { bg: "#111827", color: "#fff",     shape: "ellipse",   size: 60 },
  sancion: { bg: "#dc2626", color: "#fff",     shape: "rectangle", size: 48 },
  entidad: { bg: "#ea580c", color: "#fff",     shape: "ellipse",   size: 40 },
  fuente:  { bg: "#1d4ed8", color: "#fff",     shape: "roundrectangle", size: 44 },
  medio:   { bg: "#0369a1", color: "#fff",     shape: "roundrectangle", size: 36 },
  infra:   { bg: "#7c3aed", color: "#fff",     shape: "diamond",   size: 44 },
  ip:      { bg: "#6d28d9", color: "#fff",     shape: "diamond",   size: 32 },
};

function GrafoRelaciones({ perfil, onNodeClick, onCyCreated }) {
  const containerRef = useRef(null);
  const cyRef        = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const { nodes, edges } = construirGrafo(perfil);

    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements: { nodes, edges },
      style: [
        {
          selector: "node",
          style: {
            "background-color": (ele) => TIPO_STYLE[ele.data("tipo")]?.bg || "#6b7280",
            "color":            (ele) => TIPO_STYLE[ele.data("tipo")]?.color || "#fff",
            "shape":            (ele) => TIPO_STYLE[ele.data("tipo")]?.shape || "ellipse",
            "width":            (ele) => TIPO_STYLE[ele.data("tipo")]?.size || 40,
            "height":           (ele) => TIPO_STYLE[ele.data("tipo")]?.size || 40,
            "label":            "data(label)",
            "text-wrap":        "wrap",
            "text-max-width":   80,
            "font-size":        11,
            "text-valign":      "bottom",
            "text-margin-y":    6,
            "border-width":     (ele) => ele.data("tipo") === "sujeto" ? 3 : 0,
            "border-color":     "#fff",
            "box-shadow":       "0 2px 6px rgba(0,0,0,0.3)",
          },
        },
        {
          selector: "edge",
          style: {
            "width":           1.5,
            "line-color":      "#d1d5db",
            "target-arrow-color": "#9ca3af",
            "target-arrow-shape": "triangle",
            "curve-style":     "bezier",
            "label":           "data(label)",
            "font-size":       9,
            "color":           "#9ca3af",
            "text-rotation":   "autorotate",
            "text-margin-y":   -6,
          },
        },
        {
          selector: "node:selected",
          style: {
            "border-width": 3,
            "border-color": "#fbbf24",
            "background-opacity": 0.85,
          },
        },
      ],
      layout: {
        name:      "dagre",
        rankDir:   "TB",
        nodeSep:   60,
        rankSep:   80,
        padding:   30,
        animate:   true,
        animationDuration: 400,
      },
      userZoomingEnabled:    true,
      userPanningEnabled:    true,
      boxSelectionEnabled:   false,
      autoungrabify:         false,
    });

    cy.on("tap", "node", (evt) => {
      const node = evt.target;
      onNodeClick(node.data());
    });

    cy.on("tap", (evt) => {
      if (evt.target === cy) onNodeClick(null);
    });

    cyRef.current = cy;
    // Notificar tras finalizar la animación del layout para que cy.png() capture posiciones finales
    cy.one("layoutstop", () => { if (onCyCreated) onCyCreated(cy); });

    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [perfil]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", background: "#f8fafc", borderRadius: 8 }}
    />
  );
}

// ── Componente Timeline ──────────────────────────────────────────────────────

function Timeline({ eventos, evidencias, onAnclar }) {
  const [filtro, setFiltro] = useState("todos");

  const filtrados = eventos.filter(e => {
    if (filtro === "alertas")   return e.tipo === "alerta" || e.tipo === "cdr";
    if (filtro === "articulos") return e.tipo === "articulo";
    return true;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Filtros */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexShrink: 0 }}>
        {[["todos", "Todos"], ["alertas", "⚡ Alertas"], ["articulos", "📰 Artículos"]].map(([k, l]) => (
          <button key={k} onClick={() => setFiltro(k)} style={{
            fontSize: 11, padding: "3px 10px", borderRadius: 20,
            border: "1px solid #e5e7eb",
            background: filtro === k ? "#111827" : "#f9fafb",
            color: filtro === k ? "#fff" : "#374151",
            cursor: "pointer", fontWeight: filtro === k ? 700 : 400,
          }}>{l}</button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af", alignSelf: "center" }}>
          {filtrados.length} eventos
        </span>
      </div>

      {/* Lista */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {filtrados.length === 0 && (
          <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: 24 }}>Sin eventos en esta categoría</div>
        )}
        {filtrados.map((ev) => {
          const color  = NIVEL_COLOR[ev.nivel] || "#6b7280";
          const bg     = NIVEL_BG[ev.nivel]    || "#f9fafb";
          const anclado = evidencias.some(e => e.id === ev.id);
          return (
            <div key={ev.id} style={{
              display: "flex", gap: 10, marginBottom: 8,
              background: bg, borderRadius: 8,
              borderLeft: `3px solid ${color}`,
              padding: "8px 10px",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div>
                    {ev.fecha
                      ? <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600, display: "block", marginBottom: 2 }}>{ev.fecha}</span>
                      : <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", padding: "1px 6px", borderRadius: 4, display: "inline-block", marginBottom: 2, fontWeight: 700 }}>SIN FECHA</span>
                    }
                    {ev.url
                      ? <a href={ev.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 600, color: "#1d4ed8", textDecoration: "none", display: "block" }}>{ev.titulo}</a>
                      : <span style={{ fontSize: 12, fontWeight: 600, color: "#111827", display: "block" }}>{ev.titulo}</span>
                    }
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>{ev.fuente}</span>
                  </div>
                  <button
                    onClick={() => onAnclar(ev)}
                    title={anclado ? "Ya anclado" : "Anclar como evidencia"}
                    style={{
                      fontSize: 14, background: "none", border: "none", cursor: "pointer",
                      color: anclado ? "#ca8a04" : "#d1d5db",
                      flexShrink: 0, padding: "2px 4px",
                    }}
                  >
                    📌
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Componente Panel de Evidencias ───────────────────────────────────────────

function VisorEvidencia({ evidencia, onCerrar }) {
  if (!evidencia) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999,
    }} onClick={onCerrar}>
      <div style={{
        background: "#fff", borderRadius: 12, padding: 28,
        maxWidth: 720, width: "90%", maxHeight: "80vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
              {evidencia.fuente} · {evidencia.fecha || "Sin fecha"}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{evidencia.titulo}</div>
          </div>
          <button onClick={onCerrar} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280", marginLeft: 16 }}>✕</button>
        </div>

        {evidencia.descripcion && (
          <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, marginBottom: 16 }}>{evidencia.descripcion}</p>
        )}

        {/* Datos completos del registro (para sanciones) */}
        {evidencia.datos_completos && (
          <div style={{ background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, fontFamily: "monospace" }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: "#374151" }}>Registro completo:</div>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#111827" }}>
              {JSON.stringify(evidencia.datos_completos, null, 2)}
            </pre>
          </div>
        )}

        {evidencia.url && (
          <div style={{ marginBottom: 16 }}>
            <a href={evidencia.url} target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#1d4ed8", color: "#fff", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
              🔗 Abrir fuente original →
            </a>
          </div>
        )}

        {/* Iframe para fuentes abiertas */}
        {evidencia.url && !evidencia.url.includes("ofac") && (
          <div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>Vista previa (puede no cargar por políticas del sitio):</div>
            <iframe
              src={evidencia.url} title="Vista previa"
              style={{ width: "100%", height: 300, border: "1px solid #e5e7eb", borderRadius: 8 }}
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PanelEvidencias({ evidencias, onDesanclar, onVer, onExportar, onEtiquetaCambio }) {
  if (evidencias.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "32px 16px", color: "#9ca3af" }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>📌</div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Sin evidencias ancladas</div>
        <div style={{ fontSize: 12 }}>Haz clic en 📌 en el timeline para anclar evidencias relevantes</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{evidencias.length} evidencia{evidencias.length > 1 ? "s" : ""} anclada{evidencias.length > 1 ? "s" : ""}</span>
        <button onClick={onExportar} style={{
          fontSize: 11, padding: "4px 12px", borderRadius: 6,
          background: "#111827", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600,
        }}>
          ⬇ Exportar JSON
        </button>
      </div>

      <div style={{ overflowY: "auto", flex: 1 }}>
        {evidencias.map((ev) => {
          const color = NIVEL_COLOR[ev.nivel] || "#6b7280";
          const bg    = NIVEL_BG[ev.nivel]    || "#f9fafb";
          return (
            <div key={ev.id} style={{
              background: bg, borderRadius: 8, borderLeft: `3px solid ${color}`,
              padding: "8px 10px", marginBottom: 8,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>
                    {ev.fuente} · {ev.fecha || "Sin fecha"}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 4, wordBreak: "break-word" }}>
                    {ev.titulo}
                  </div>
                  <EtiquetaEvidencia
                    evId={ev.id}
                    etiquetaInicial={ev.etiqueta || "RELEVANTE"}
                    onCambio={onEtiquetaCambio}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginLeft: 8, flexShrink: 0 }}>
                  <button onClick={() => onVer(ev)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: "#1d4ed8", color: "#fff", border: "none", cursor: "pointer" }}>Ver</button>
                  <button onClick={() => onDesanclar(ev.id)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", cursor: "pointer" }}>✕</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EtiquetaEvidencia({ evId, etiquetaInicial, onCambio }) {
  const ETIQUETAS = ["RELEVANTE", "CONFIRMA", "DESCARTA"];
  const [etiqueta, setEtiqueta] = useState(etiquetaInicial);
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
      {ETIQUETAS.map(e => (
        <button key={e} onClick={() => { setEtiqueta(e); onCambio && onCambio(evId, e); }}
          style={{
            fontSize: 9, padding: "2px 6px", borderRadius: 4,
            background: etiqueta === e
              ? (e === "CONFIRMA" ? "#16a34a" : e === "DESCARTA" ? "#dc2626" : "#1d4ed8")
              : "#f3f4f6",
            color: etiqueta === e ? "#fff" : "#6b7280",
            border: "none", cursor: "pointer", fontWeight: 700,
          }}
        >{e}</button>
      ))}
    </div>
  );
}

// ── Panel detalle de nodo del grafo ─────────────────────────────────────────

function NodeDetailPanel({ nodo, onCerrar, onAnclarNodo }) {
  if (!nodo) return (
    <div style={{ fontSize: 12, color: "#9ca3af", padding: 16, textAlign: "center" }}>
      Haz clic en un nodo del grafo para ver su detalle
    </div>
  );

  const color = NIVEL_COLOR[nodo.nivel] || "#6b7280";
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{nodo.label}</div>
        <button onClick={onCerrar} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 16 }}>✕</button>
      </div>
      <div style={{ fontSize: 11, color, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
        {nodo.nivel} · {nodo.tipo}
      </div>
      {nodo.detalle    && <div style={{ fontSize: 12, color: "#374151", marginBottom: 4 }}>{nodo.detalle}</div>}
      {nodo.extracto   && <p  style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, marginBottom: 8 }}>{nodo.extracto}</p>}
      {nodo.datasets   && <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>Fuentes: {nodo.datasets}</div>}
      {nodo.programa   && <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>Programa: {nodo.programa}</div>}
      {nodo.tipo === "ip" && <div style={{ fontSize: 12, fontFamily: "monospace", color: "#7c3aed", marginBottom: 4 }}>{nodo.label}</div>}

      {nodo.url_detalle && (
        <a href={nodo.url_detalle} target="_blank" rel="noreferrer"
          style={{ display: "inline-block", marginTop: 8, fontSize: 12, color: "#1d4ed8", textDecoration: "none", background: "#eff6ff", padding: "4px 10px", borderRadius: 6 }}>
          Ver registro completo →
        </a>
      )}
      {!nodo.url_detalle && nodo.tipo === "sancion" && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280", background: "#f9fafb", borderRadius: 6, padding: 10 }}>
          Los datos completos del registro están disponibles en la sección Sanciones de la Vista Resumen.
        </div>
      )}
      <button onClick={() => onAnclarNodo(nodo)}
        style={{ marginTop: 12, width: "100%", background: "#111827", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
        📌 Anclar como evidencia
      </button>
    </div>
  );
}

// ── Dashboard principal ──────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8001/api/v1";

export default function OsintDashboard({ perfil }) {
  const [nodoSeleccionado, setNodoSeleccionado] = useState(null);
  const [evidencias, setEvidencias]             = useState([]);
  const [visorAbierto, setVisorAbierto]         = useState(null);
  const [exportando, setExportando]             = useState(false);
  const cyInstanceRef                            = useRef(null);
  const timeline                                 = construirTimeline(perfil);
  const nivel                                    = perfil.nivel_riesgo || "BAJO";
  const color_nivel                              = NIVEL_COLOR[nivel];
  const sesion_key                               = perfil.termino || "sin_termino";

  // Cargar evidencias persistidas al montar
  useEffect(() => {
    fetch(`${API_BASE}/evidencias?sesion=${encodeURIComponent(sesion_key)}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const mapeadas = data.map(e => ({
          id:              e.id,
          titulo:          e.titulo,
          url:             e.url,
          tipo:            e.tipo,
          etiqueta:        e.etiqueta || "RELEVANTE",
          fuente:          e.tipo,
          fecha:           e.fecha_pin ? e.fecha_pin.substring(0, 10) : null,
          nivel:           e.datos?.nivel || "BAJO",
          descripcion:     e.datos?.extracto || e.datos?.detalle || "",
          datos_completos: e.datos || null,
        }));
        setEvidencias(mapeadas);
      })
      .catch(() => {});
  }, [sesion_key]);

  const anclar = useCallback(async (ev) => {
    // No duplicar por título + tipo
    if (evidencias.some(e => e.titulo === ev.titulo && e.tipo === ev.tipo)) return;
    try {
      const res = await fetch(`${API_BASE}/evidencias`, {
        method:      "POST",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body: JSON.stringify({
          sesion_busqueda: sesion_key,
          tipo:    ev.tipo || "otro",
          titulo:  ev.titulo,
          url:     ev.url || null,
          etiqueta: "RELEVANTE",
          datos:   ev.datos_completos || null,
        }),
      });
      if (res.ok) {
        const { id } = await res.json();
        setEvidencias(prev => [...prev, { ...ev, id, etiqueta: "RELEVANTE" }]);
      }
    } catch {
      // Fallback sin BD (offline)
      setEvidencias(prev => [...prev, { ...ev, etiqueta: ev.etiqueta || "RELEVANTE" }]);
    }
  }, [evidencias, sesion_key]);

  const anclarNodo = useCallback((nodo) => {
    const ev = {
      id:          `nodo_${nodo.id}`,
      titulo:      nodo.label,
      fuente:      nodo.tipo,
      fecha:       null,
      url:         nodo.url_detalle || null,
      descripcion: nodo.detalle || nodo.extracto || "",
      nivel:       nodo.nivel || "BAJO",
      tipo:        "nodo",
      datos_completos: nodo,
    };
    anclar(ev);
  }, [anclar]);

  const desanclar = useCallback(async (id) => {
    try {
      await fetch(`${API_BASE}/evidencias/${id}`, { method: "DELETE", credentials: "include" });
    } catch {}
    setEvidencias(prev => prev.filter(e => e.id !== id));
  }, []);

  const cambiarEtiqueta = useCallback(async (id, etiqueta) => {
    try {
      await fetch(`${API_BASE}/evidencias/${id}/etiqueta`, {
        method:      "PATCH",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body: JSON.stringify({ etiqueta }),
      });
    } catch {}
    setEvidencias(prev => prev.map(e => e.id === id ? { ...e, etiqueta } : e));
  }, []);

  function exportarEvidencias() {
    const blob = new Blob(
      [JSON.stringify({ sujeto: perfil.termino, nivel_riesgo: perfil.nivel_riesgo, evidencias }, null, 2)],
      { type: "application/json" }
    );
    const url = URL.createObjectURL(blob);
    const a   = document.createElement("a");
    a.href = url;
    a.download = `evidencias_${perfil.termino.replace(/\s+/g, "_")}_${new Date().toISOString().substring(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function generarProducto() {
    if (!cyInstanceRef.current) {
      alert("El grafo todavía está cargando. Espere un momento e intente de nuevo.");
      return;
    }
    setExportando(true);
    try {
      // Captura del grafo como PNG base64 (cyInstanceRef garantizado por layoutstop)
      let grafoPng = "";
      try {
        grafoPng = cyInstanceRef.current.png({ output: "base64uri", scale: 2, full: true, bg: "#f8fafc" });
      } catch (e) {
        console.warn("cy.png() falló, el grafo se omitirá en el export:", e);
      }

      const fecha     = new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" });
      const horaGen   = new Date().toLocaleTimeString("es-MX");
      const isoGen    = new Date().toISOString();

      // SHA-256 real del contenido clave del documento (Web Crypto API)
      const _contenidoCustodia = [
        perfil.termino || "", perfil.alias || "", String(perfil.score_riesgo || 0),
        nivel, isoGen,
        String([...(perfil.alertas||[]), ...(perfil.alertas_cdr||[])].length),
        String(timeline.length), String(evidencias.length),
        perfil.reporte_ia?.analisis_ia?.substring(0, 200) || "",
      ].join("|");
      const _enc = new TextEncoder().encode(_contenidoCustodia);
      const _hashBuf = await crypto.subtle.digest("SHA-256", _enc);
      const sha256hex = Array.from(new Uint8Array(_hashBuf))
        .map(b => b.toString(16).padStart(2, "0")).join("");
      const termino   = perfil.termino || "sin_termino";
      const alias     = perfil.alias   || "";
      const score     = perfil.score_riesgo || 0;
      const nivelRiesgo = nivel;
      const colorNivel  = color_nivel;

      // ── Sección: Alertas ──
      const alertasRows = [...(perfil.alertas || []), ...(perfil.alertas_cdr || [])].map(a => `
        <tr>
          <td style="padding:6px 10px;font-weight:700;color:${NIVEL_COLOR[a.nivel]||'#6b7280'};white-space:nowrap">${a.nivel||"INFO"}</td>
          <td style="padding:6px 10px;color:#111827">${a.mensaje}</td>
          <td style="padding:6px 10px;color:#6b7280;font-size:11px">${a.fuente||""}</td>
        </tr>`).join("") || `<tr><td colspan="3" style="padding:12px;color:#9ca3af;text-align:center">Sin alertas registradas</td></tr>`;

      // ── Sección: Timeline ──
      const timelineRows = timeline.map(ev => `
        <tr style="border-bottom:1px solid #f3f4f6">
          <td style="padding:6px 10px;white-space:nowrap;font-size:11px;color:#9ca3af">${ev.fecha || "Sin fecha"}</td>
          <td style="padding:6px 10px;font-size:11px;color:#6b7280">${ev.tipo}</td>
          <td style="padding:6px 10px;font-size:12px;color:#111827;font-weight:600">
            ${ev.url ? `<a href="${ev.url}" style="color:#1d4ed8">${ev.titulo}</a>` : ev.titulo}
          </td>
          <td style="padding:6px 10px;font-size:11px;color:#6b7280">${ev.fuente||""}</td>
        </tr>`).join("") || `<tr><td colspan="4" style="padding:12px;color:#9ca3af;text-align:center">Sin eventos en el timeline</td></tr>`;

      // ── Sección: Evidencias ──
      const ETIQ_COLOR = { CONFIRMA: "#16a34a", DESCARTA: "#dc2626", RELEVANTE: "#1d4ed8" };
      const evidenciasRows = evidencias.map(ev => `
        <tr style="border-bottom:1px solid #f3f4f6">
          <td style="padding:8px 10px">
            <span style="background:${ETIQ_COLOR[ev.etiqueta]||'#6b7280'};color:#fff;font-size:10px;font-weight:800;padding:2px 8px;border-radius:99px">
              ${ev.etiqueta||"RELEVANTE"}
            </span>
          </td>
          <td style="padding:8px 10px;font-size:12px;color:#111827;font-weight:600">
            ${ev.url ? `<a href="${ev.url}" style="color:#1d4ed8">${ev.titulo}</a>` : ev.titulo}
          </td>
          <td style="padding:8px 10px;font-size:11px;color:#6b7280">${ev.fuente||ev.tipo||""}</td>
          <td style="padding:8px 10px;font-size:11px;color:#9ca3af;white-space:nowrap">${ev.fecha||"Sin fecha"}</td>
        </tr>`).join("") || `<tr><td colspan="4" style="padding:12px;color:#9ca3af;text-align:center">Sin evidencias ancladas</td></tr>`;

      // ── Sección: Fuentes consultadas ──
      const fuentesBadges = [
        perfil.fuentes?.ofac?.en_lista_ofac   ? `<span style="background:#dc2626;color:#fff;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">OFAC SDN</span>` : "",
        perfil.fuentes?.opensanctions?.total  ? `<span style="background:#ea580c;color:#fff;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">OpenSanctions (${perfil.fuentes.opensanctions.total})</span>` : "",
        perfil.fuentes?.wikipedia?.encontrado ? `<span style="background:#1d4ed8;color:#fff;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">Wikipedia</span>` : "",
        (perfil.fuentes?.articulos?.length||0) > 0 ? `<span style="background:#0369a1;color:#fff;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">Medios (${perfil.fuentes.articulos.length} arts.)</span>` : "",
        perfil.herramientas_externas?.harvester?.disponible ? `<span style="background:#7c3aed;color:#fff;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">theHarvester</span>` : "",
        perfil.fuentes?.shodan ? `<span style="background:#0f172a;color:#fff;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">Shodan</span>` : "",
      ].filter(Boolean).join(" ");

      const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Producto de Inteligencia — ${termino} — ${fecha}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #f1f5f9; color: #111827; }
    .page { max-width: 1100px; margin: 0 auto; padding: 32px 24px; }

    /* Cabecera clasificación */
    .clasificacion-header {
      background: #0f172a; color: #f8fafc;
      text-align: center; padding: 10px 0; font-size: 13px;
      font-weight: 800; letter-spacing: 3px; text-transform: uppercase;
    }
    .clasificacion-footer {
      background: #0f172a; color: #f8fafc;
      text-align: center; padding: 10px 0; font-size: 12px;
      font-weight: 700; letter-spacing: 2px; margin-top: 32px;
    }

    /* Header institucional */
    .inst-header {
      background: #fff; border-radius: 12px;
      padding: 20px 28px; margin: 16px 0;
      display: flex; justify-content: space-between; align-items: center;
      border-left: 6px solid ${colorNivel};
      box-shadow: 0 1px 6px rgba(0,0,0,.08);
    }
    .inst-header .sujeto-nombre { font-size: 26px; font-weight: 800; color: #111827; }
    .inst-header .sujeto-alias  { font-size: 14px; color: #6b7280; font-weight: 400; margin-left: 10px; }
    .inst-header .meta          { font-size: 12px; color: #9ca3af; margin-top: 4px; }
    .score-box { text-align: right; }
    .score-box .score-num { font-size: 48px; font-weight: 900; color: ${colorNivel}; line-height: 1; }
    .score-box .score-lbl { font-size: 11px; color: #9ca3af; }
    .nivel-badge {
      display: inline-block; margin-top: 6px;
      background: ${colorNivel}; color: #fff;
      font-size: 12px; font-weight: 800; padding: 3px 14px;
      border-radius: 99px; letter-spacing: 1px;
    }

    /* Secciones */
    .seccion {
      background: #fff; border-radius: 12px;
      margin-bottom: 20px; overflow: hidden;
      box-shadow: 0 1px 6px rgba(0,0,0,.06);
    }
    .seccion-titulo {
      padding: 14px 22px; border-bottom: 1px solid #e5e7eb;
      font-weight: 700; font-size: 15px; color: #111827;
      display: flex; align-items: center; gap: 8px;
      background: #f8fafc;
    }
    .seccion-body { padding: 20px 22px; }

    /* Grafo */
    .grafo-img { width: 100%; border-radius: 8px; border: 1px solid #e5e7eb; }

    /* Tablas */
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead th {
      background: #f8fafc; padding: 10px 10px;
      text-align: left; font-size: 11px; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280;
      border-bottom: 1px solid #e5e7eb;
    }
    tbody tr:hover { background: #f9fafb; }

    /* Fuentes badges */
    .fuentes-wrap { display: flex; flex-wrap: wrap; gap: 8px; padding: 16px 22px; }

    /* Footer hash */
    .hash-box {
      background: #f8fafc; border: 1px solid #e5e7eb;
      border-radius: 8px; padding: 14px 18px; font-size: 11px;
      color: #6b7280; font-family: monospace; margin-top: 4px;
    }

    @media print {
      body { background: white; }
      .page { padding: 0; }
      .clasificacion-header, .clasificacion-footer { border-radius: 0; }
    }
  </style>
</head>
<body>

<div class="clasificacion-header">PRODUCTO DE INTELIGENCIA — USO OFICIAL — ZLINKINT</div>

<div class="page">

  <!-- ── ENCABEZADO SUJETO ── -->
  <div class="inst-header">
    <div>
      <div class="sujeto-nombre">${termino}${alias ? `<span class="sujeto-alias">"${alias}"</span>` : ""}</div>
      <div class="meta">
        Generado: ${fecha} ${horaGen} &nbsp;|&nbsp;
        Analista: ZlinkINT OSINT &nbsp;|&nbsp;
        SHA-256: <strong style="font-family:monospace;font-size:11px;word-break:break-all">${sha256hex}</strong>
      </div>
    </div>
    <div class="score-box">
      <div class="score-num">${score}</div>
      <div class="score-lbl">score / 100</div>
      <span class="nivel-badge">${nivelRiesgo}</span>
    </div>
  </div>

  <!-- ── FUENTES CONSULTADAS ── -->
  <div class="seccion">
    <div class="seccion-titulo">🔍 Fuentes Consultadas</div>
    <div class="fuentes-wrap">${fuentesBadges || '<span style="color:#9ca3af;font-size:13px;padding:4px 0">Sin fuentes activas registradas</span>'}</div>
  </div>

  <!-- ── ALERTAS ── -->
  <div class="seccion">
    <div class="seccion-titulo">⚡ Alertas de Inteligencia</div>
    <div class="seccion-body" style="padding:0">
      <table>
        <thead><tr><th>Nivel</th><th>Descripción</th><th>Fuente</th></tr></thead>
        <tbody>${alertasRows}</tbody>
      </table>
    </div>
  </div>

  <!-- ── GRAFO DE RELACIONES ── -->
  <div class="seccion">
    <div class="seccion-titulo">🕸️ Grafo de Relaciones</div>
    <div class="seccion-body">
      ${grafoPng
        ? `<img src="${grafoPng}" alt="Grafo de relaciones" class="grafo-img">`
        : `<div style="padding:32px;text-align:center;color:#9ca3af;border:2px dashed #e5e7eb;border-radius:8px">Grafo no disponible — abrir el dashboard para generarlo</div>`
      }
      <div style="font-size:11px;color:#9ca3af;margin-top:8px">
        ● Sujeto &nbsp;&nbsp; ■ Sanción &nbsp;&nbsp; ◆ Infraestructura &nbsp;&nbsp; ⬡ Fuente/Medio
      </div>
    </div>
  </div>

  <!-- ── TIMELINE ── -->
  <div class="seccion">
    <div class="seccion-titulo">📅 Timeline Cronológico <span style="font-size:12px;font-weight:400;color:#9ca3af;margin-left:8px">${timeline.length} eventos</span></div>
    <div class="seccion-body" style="padding:0">
      <table>
        <thead><tr><th>Fecha</th><th>Tipo</th><th>Evento</th><th>Fuente</th></tr></thead>
        <tbody>${timelineRows}</tbody>
      </table>
    </div>
  </div>

  <!-- ── EVIDENCIAS ANCLADAS ── -->
  <div class="seccion">
    <div class="seccion-titulo">📎 Evidencias Ancladas <span style="font-size:12px;font-weight:400;color:#9ca3af;margin-left:8px">${evidencias.length} registros</span></div>
    <div class="seccion-body" style="padding:0">
      <table>
        <thead><tr><th>Etiqueta</th><th>Evidencia</th><th>Tipo</th><th>Fecha</th></tr></thead>
        <tbody>${evidenciasRows}</tbody>
      </table>
    </div>
  </div>

  <!-- ── ANÁLISIS IA ── -->
  ${perfil.reporte_ia ? `
  <div class="seccion">
    <div class="seccion-titulo">🤖 Análisis de Inteligencia Artificial${perfil.reporte_ia?.dual_ia ? " — Análisis Dual" : ""}</div>
    <div class="seccion-body">
      ${perfil.reporte_ia.dual_ia ? `
        <div style="background:#f0fdf4;border-left:4px solid #16a34a;padding:14px 18px;border-radius:6px;margin-bottom:16px">
          <div style="font-size:11px;font-weight:800;color:#15803d;letter-spacing:1px;margin-bottom:8px">ANÁLISIS IA — PRINCIPAL</div>
          <div style="font-size:13px;color:#111827;line-height:1.7;white-space:pre-wrap">${perfil.reporte_ia.analisis_ia || ""}</div>
        </div>
        <div style="background:#f0f9ff;border-left:4px solid #0369a1;padding:14px 18px;border-radius:6px">
          <div style="font-size:11px;font-weight:800;color:#0369a1;letter-spacing:1px;margin-bottom:8px">ANÁLISIS GEMINI (GOOGLE)</div>
          <div style="font-size:13px;color:#111827;line-height:1.7;white-space:pre-wrap">${perfil.reporte_ia.analisis_gemini || ""}</div>
        </div>
      ` : `
        <div style="background:#f0fdf4;border-left:4px solid #16a34a;padding:14px 18px;border-radius:6px">
          <div style="font-size:11px;font-weight:800;color:#15803d;letter-spacing:1px;margin-bottom:8px">ANÁLISIS IA</div>
          <div style="font-size:13px;color:#111827;line-height:1.7;white-space:pre-wrap">${typeof perfil.reporte_ia === "string" ? perfil.reporte_ia : (perfil.reporte_ia.analisis_ia || perfil.reporte_ia.resumen || JSON.stringify(perfil.reporte_ia))}</div>
        </div>
      `}
    </div>
  </div>` : ""}

  <!-- ── CADENA DE CUSTODIA ── -->
  <div class="seccion">
    <div class="seccion-titulo">🔐 Cadena de Custodia</div>
    <div class="seccion-body">
      <div class="hash-box">
        DOCUMENTO: Producto de Inteligencia OSINT — ZlinkINT<br>
        SUJETO: ${termino}<br>
        NIVEL DE RIESGO: ${nivelRiesgo} (Score: ${score}/100)<br>
        TOTAL ALERTAS: ${[...(perfil.alertas||[]), ...(perfil.alertas_cdr||[])].length}<br>
        TOTAL EVENTOS TIMELINE: ${timeline.length}<br>
        TOTAL EVIDENCIAS ANCLADAS: ${evidencias.length}<br>
        ANÁLISIS IA: ${perfil.reporte_ia ? (perfil.reporte_ia.dual_ia ? "Motor IA (dual)" : "Disponible") : "No generado"}<br>
        GENERADO: ${fecha} ${horaGen}<br>
        SHA-256: ${sha256hex}<br>
        PLATAFORMA: ZlinkINT OSINT v1.0<br>
        CLASIFICACIÓN: USO OFICIAL — PROHIBIDA DIFUSIÓN SIN AUTORIZACIÓN
      </div>
    </div>
  </div>

</div><!-- /page -->

<div class="clasificacion-footer">PLATAFORMA ZLINKINT — MÓDULO OSINT — DOCUMENTO DE INTELIGENCIA OFICIAL</div>

</body>
</html>`;

      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `PI_OSINT_${termino.replace(/\s+/g, "_")}_${new Date().toISOString().substring(0, 10)}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportando(false);
    }
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }}>

      {/* Visor modal */}
      {visorAbierto && <VisorEvidencia evidencia={visorAbierto} onCerrar={() => setVisorAbierto(null)} />}

      {/* Encabezado */}
      <div style={{
        background: "#fff", borderRadius: 12, padding: "16px 24px",
        boxShadow: "0 1px 4px rgba(0,0,0,.08)", marginBottom: 16,
        borderLeft: `5px solid ${color_nivel}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>
            {perfil.termino}
            {perfil.alias && <span style={{ fontSize: 14, color: "#6b7280", fontWeight: 400, marginLeft: 10 }}>"{perfil.alias}"</span>}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            Producto de Inteligencia · {new Date().toLocaleDateString("es-MX")} · {timeline.length} eventos
          </div>
        </div>
        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <div>
            <div style={{ fontSize: 32, fontWeight: 800, color: color_nivel, lineHeight: 1 }}>{perfil.score_riesgo}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>score / 100</div>
            <span style={{ display: "inline-block", marginTop: 4, background: color_nivel, color: "#fff", fontSize: 11, fontWeight: 800, padding: "2px 10px", borderRadius: 99, letterSpacing: 1 }}>
              {nivel}
            </span>
          </div>
          <button
            onClick={generarProducto}
            disabled={exportando}
            style={{
              background: exportando ? "#6b7280" : "#111827",
              color: "#fff", border: "none", borderRadius: 8,
              padding: "8px 16px", fontSize: 12, fontWeight: 700,
              cursor: exportando ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {exportando ? "⏳ Generando..." : "📄 Generar Producto"}
          </button>
        </div>
      </div>

      {/* Layout 2 columnas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 16, alignItems: "start" }}>

        {/* Columna izquierda: grafo */}
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>🕸️ Grafo de Relaciones</div>
            <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#6b7280" }}>
              {[["sujeto","Sujeto"], ["sancion","Sanción"], ["fuente","Fuente"], ["infra","Infraestructura"]].map(([t, l]) => (
                <span key={t} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: TIPO_STYLE[t]?.bg, display: "inline-block" }} />
                  {l}
                </span>
              ))}
            </div>
          </div>
          <div style={{ height: 460, position: "relative" }}>
            <GrafoRelaciones perfil={perfil} onNodeClick={setNodoSeleccionado} onCyCreated={cy => { cyInstanceRef.current = cy; }} />
            {/* Panel detalle de nodo */}
            {nodoSeleccionado && (
              <div style={{
                position: "absolute", top: 10, right: 10, width: 240,
                background: "#fff", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                zIndex: 100,
              }}>
                <NodeDetailPanel nodo={nodoSeleccionado} onCerrar={() => setNodoSeleccionado(null)} onAnclarNodo={anclarNodo} />
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha: timeline + evidencias */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Timeline */}
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>📅 Timeline</div>
            </div>
            <div style={{ padding: 14, height: 300 }}>
              <Timeline eventos={timeline} evidencias={evidencias} onAnclar={anclar} />
            </div>
          </div>

          {/* Panel de evidencias */}
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>📎 Evidencias ancladas</div>
            </div>
            <div style={{ padding: 14, minHeight: 180 }}>
              <PanelEvidencias
                evidencias={evidencias}
                onDesanclar={desanclar}
                onVer={setVisorAbierto}
                onExportar={exportarEvidencias}
                onEtiquetaCambio={cambiarEtiqueta}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
